import type { LeagueCard, LeagueStudentRef } from "@core/league/types";

/**
 * One action can return several outcomes (a quiz: completed + passed +
 * excellent), each with its own card. Merge per §4 of the contract:
 * pointsDelta = sum, rankBefore = first card's, rankAfter / zone /
 * weekPoints / monthRank / groupSize = last card's, passed = union by id
 * (max 3, order kept).
 */
export function mergeLeagueCards(
  cards: Array<LeagueCard | null | undefined>,
): LeagueCard | null {
  const present = cards.filter((card): card is LeagueCard => Boolean(card));

  if (present.length === 0) return null;
  if (present.length === 1) return present[0];

  const first = present[0];
  const last = present[present.length - 1];
  const passed: LeagueStudentRef[] = [];

  for (const card of present) {
    for (const student of card.passed) {
      if (passed.length < 3 && !passed.some((item) => item.id === student.id)) {
        passed.push(student);
      }
    }
  }

  return {
    source: last.source,
    pointsDelta: present.reduce((sum, card) => sum + card.pointsDelta, 0),
    weekPoints: last.weekPoints,
    rankBefore: first.rankBefore,
    rankAfter: last.rankAfter,
    groupSize: last.groupSize,
    zone: last.zone,
    passed,
    monthRank: last.monthRank,
  };
}

export type LeagueCardLine = {
  key: string;
  params: { name: string; rank: number | string };
};

/** One headline for the after-activity card. */
export function leagueCardLine(card: LeagueCard): LeagueCardLine {
  const name = card.passed[0]?.name ?? "";
  const rank = card.rankAfter ?? "";

  if (card.passed.length > 0 && name) {
    return { key: "league.card.passed", params: { name, rank } };
  }

  if (card.rankBefore === null && card.rankAfter !== null) {
    return { key: "league.card.entered", params: { name, rank } };
  }

  if (
    card.rankBefore !== null &&
    card.rankAfter !== null &&
    card.rankAfter < card.rankBefore
  ) {
    return { key: "league.card.rankUp", params: { name, rank } };
  }

  return { key: "league.card.keepGoing", params: { name, rank } };
}

/** A card is worth showing only when it carries points. */
export function isLeagueCardVisible(card: LeagueCard | null): card is LeagueCard {
  return Boolean(card && card.pointsDelta > 0);
}
